import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Lock, ArrowLeft } from "lucide-react";
import api from "../api";
import { MonkeyMark } from "../components/Logo";

export default function AdminLogin() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      localStorage.setItem("kitoe_token", data.token);
      navigate("/admin");
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Login failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="min-h-screen flex items-center justify-center px-4 py-16">
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        className="w-full max-w-md rounded-[2rem] border-2 border-ink bg-white shadow-brutal-lg p-8 sm:p-10"
      >
        <div className="flex justify-center">
          <MonkeyMark size={72} />
        </div>
        <h1 className="mt-4 text-center font-display font-black text-2xl sm:text-3xl tracking-tight">
          Studio Admin
        </h1>
        <p className="mt-1.5 text-center text-sm text-ink/60">
          Sign in to manage your games
        </p>

        <form onSubmit={submit} className="mt-8 space-y-4">
          <div>
            <label htmlFor="admin-email" className="block text-xs font-extrabold uppercase tracking-widest text-ink/60 mb-1.5">
              Email
            </label>
            <input
              id="admin-email"
              data-testid="admin-login-email-input"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-4 py-3 rounded-2xl border-2 border-ink bg-cream font-medium focus:outline-none focus:ring-2 focus:ring-coral"
              placeholder="you@example.com"
            />
          </div>
          <div>
            <label htmlFor="admin-password" className="block text-xs font-extrabold uppercase tracking-widest text-ink/60 mb-1.5">
              Password
            </label>
            <input
              id="admin-password"
              data-testid="admin-login-password-input"
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-4 py-3 rounded-2xl border-2 border-ink bg-cream font-medium focus:outline-none focus:ring-2 focus:ring-coral"
              placeholder="••••••••"
            />
          </div>

          {error && (
            <p data-testid="admin-login-error" className="text-sm font-bold text-coral">
              {error}
            </p>
          )}

          <button
            type="submit"
            data-testid="admin-login-submit-button"
            disabled={loading}
            className="w-full inline-flex items-center justify-center gap-2 px-6 py-4 rounded-2xl bg-ink text-cream font-display font-extrabold text-lg border-2 border-ink shadow-brutal-sm hover:-translate-y-0.5 hover:shadow-brutal hover:bg-coral transition-all duration-200 disabled:opacity-60"
          >
            <Lock size={18} /> {loading ? "Signing in…" : "Sign in"}
          </button>
        </form>

        <Link
          to="/"
          data-testid="admin-login-back-link"
          className="mt-6 inline-flex items-center gap-1.5 text-sm font-bold text-ink/60 hover:text-ink transition-colors"
        >
          <ArrowLeft size={14} /> Back to website
        </Link>
      </motion.div>
    </main>
  );
}
